import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Sparkles, Loader2, X, Plus, Download, Printer, Award, ChevronRight } from "lucide-react";
import api, { apiErr, fmtDate, downloadDoc } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, Th, Td, Stat } from "@/components/admin/ui";

const STAGES = ["Referral Submitted", "Pending Host Review", "Approved for Invitation", "Invitation Sent",
  "Invitation Viewed", "Invitation Accepted", "Access Requested", "Pending Activation", "Access Approved",
  "Account Activated", "Reservation Requested", "Reservation Approved", "First Stay Completed",
  "Declined", "Expired", "Withdrawn", "Restricted"];
const RECOGNITION_TYPES = ["Personal Thank You", "Complimentary Add-On", "Invitation Privilege",
  "Membership Note", "Private Recognition", "Custom Benefit"];
const inputCls = "w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-gold focus:outline-none";

export default function AdminReferrals() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [stage, setStage] = useState("");
  const [f, setF] = useState({ preset: "", member: "", level: "", source: "", invitation_status: "", access_status: "", account_status: "", reservation_status: "", first_stay: "", follow_up: "", recognition_status: "" });
  const [pdfVariant, setPdfVariant] = useState("summary");
  const qs = () => {
    const p = new URLSearchParams();
    if (stage) p.set("stage", stage);
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  };
  const [detail, setDetail] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ referring_member_name: "", referred_name: "", referred_email: "", relationship: "", suggested_experience: "", reason: "" });
  const [rec, setRec] = useState({ recognition_type: "Personal Thank You", note: "" });
  const [reviews, setReviews] = useState([]);

  const loadReviews = () => api.get("/admin/referral-link-reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));

  const load = () => {
    const q = qs();
    api.get(`/admin/referrals${q ? `?${q}` : ""}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/admin/referrals/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); loadReviews(); /* eslint-disable-next-line */ }, [stage, f]);

  const openDetail = async (r) => {
    setAi(null); setRec({ recognition_type: "Personal Thank You", note: "" });
    try { const { data } = await api.get(`/admin/referrals/${r.id}`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const advance = async (newStage) => {
    try { const { data } = await api.post(`/admin/referrals/${detail.id}/stage`, { stage: newStage }); setDetail({ ...detail, ...data }); toast.success(`Stage → ${newStage}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const summarize = async () => {
    setAiBusy(true); setAi(null);
    try { const { data } = await api.post(`/admin/referrals/${detail.id}/ai-summarize`); setAi({ label: data.label, text: data.suggestion }); }
    catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };
  const createRef = async () => {
    if (!nf.referred_name) { toast.error("Referred person name is required."); return; }
    try { await api.post("/admin/referrals", nf); toast.success("Referral created."); setShowNew(false); setNf({ referring_member_name: "", referred_name: "", referred_email: "", relationship: "", suggested_experience: "", reason: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addRecognition = async () => {
    if (!rec.note) { toast.error("A note is required."); return; }
    try {
      await api.post("/admin/recognitions", { referral_id: detail.id, member_name: detail.referring_member_name, ...rec });
      toast.success("Recognition recorded (private).");
      openDetail(detail); setRec({ recognition_type: "Personal Thank You", note: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (rows === null) return <Loading />;

  const resolveReview = async (rv, action, referral_id) => {
    try { await api.post(`/admin/referral-link-reviews/${rv.id}/resolve`, { action, referral_id }); toast.success(action === "link" ? "Linked." : "Review resolved."); loadReviews(); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-6xl" data-testid="admin-referrals">
      <AdminHeading title="Referral Tracking" subtitle="How members' referrals progress through invitation, access and stays. Private — never public.">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={pdfVariant} onChange={(e) => setPdfVariant(e.target.value)} className="text-xs bg-black/40 border border-white/15 rounded-full px-3 py-2 text-muted-foreground" data-testid="pdf-variant">
            {[["summary", "Summary + Funnel"], ["by_member", "By Member"], ["by_level", "By Level"], ["pending", "Pending"], ["follow_up", "Follow-Up"], ["expired", "Expired"], ["approved_access", "Approved Access"], ["account_activation", "Activations"], ["reservation", "Reservations"], ["first_stay", "First Stays"], ["recognition", "Recognition"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => downloadDoc(`/admin/referrals/report.csv?${qs()}`, "referrals.csv")} className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid="referrals-csv"><Download size={14} /> CSV</button>
          <button onClick={() => downloadDoc(`/admin/referrals-report.pdf?report=${pdfVariant}&${qs()}`, `referral-${pdfVariant}.pdf`)} className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid="referrals-pdf"><Download size={14} /> PDF</button>
          <button onClick={() => window.print()} className="btn-ghost-gold px-3 py-2 text-xs inline-flex items-center gap-1.5" data-testid="referrals-print"><Printer size={14} /> Print</button>
          <button onClick={() => setShowNew(true)} className="btn-gold px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid="referral-new"><Plus size={14} /> New Referral</button>
        </div>
      </AdminHeading>

      {reviews.length > 0 && (
        <Panel title={`Referral Link Review (${reviews.length})`} className="mb-6">
          <div className="space-y-3" data-testid="link-review-queue">
            {reviews.map((rv) => (
              <div key={rv.id} className="glass rounded-lg p-3" data-testid={`link-review-${rv.id}`}>
                <p className="text-sm"><span className="text-amber-300">{rv.reason}</span> — <span className="text-muted-foreground">{rv.email}</span></p>
                <p className="text-[11px] text-muted-foreground mt-1">D flagged this — a human must choose the correct link. D cannot link ambiguous records.</p>
                <div className="mt-2 space-y-1.5">
                  {(rv.candidates || []).map((c) => (
                    <div key={c.id} className="flex items-center gap-2 text-sm">
                      <span className="flex-1">{c.referring_member_name || "?"} → {c.referred_name} <span className="text-[10px] text-muted-foreground">({c.stage})</span></span>
                      <button onClick={() => resolveReview(rv, "link", c.id)} className="btn-gold px-2.5 py-1 text-[11px]" data-testid={`link-review-pick-${c.id}`}>Link this</button>
                    </div>
                  ))}
                  <button onClick={() => resolveReview(rv, "reject")} className="text-[11px] text-rose-300 hover:underline mt-1">Reject / resolve without linking</button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="referral-stats">
          <Stat label="Total Referrals" value={stats.total} accent="gold" />
          <Stat label="Invitations Sent" value={stats.invitations_sent} accent="pool" />
          <Stat label="Accounts Activated" value={stats.activated} accent="gold" />
          <Stat label="Conversion Rate" value={`${stats.conversion_rate}%`} accent="pool" />
        </div>
      )}

      <div className="glass rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="referral-filters">
        <select value={f.preset} onChange={(e) => setF({ ...f, preset: e.target.value })} className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5" data-testid="filter-preset">
          {[["", "All time"], ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"], ["last_week", "Last Week"], ["this_month", "This Month"], ["last_month", "Last Month"], ["quarter", "This Quarter"], ["prev_quarter", "Prev Quarter"], ["ytd", "Year to Date"], ["prev_year", "Prev Year"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={f.member} onChange={(e) => setF({ ...f, member: e.target.value })} placeholder="Referring member" className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5 w-36" data-testid="filter-member" />
        <input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="Source" className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5 w-28" />
        <select value={f.invitation_status} onChange={(e) => setF({ ...f, invitation_status: e.target.value })} className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5">
          {[["", "Invitation: any"], ["sent", "Invite Sent+"], ["viewed", "Invite Viewed+"], ["accepted", "Invite Accepted+"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={f.access_status} onChange={(e) => setF({ ...f, access_status: e.target.value })} className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5">
          {[["", "Access: any"], ["requested", "Requested+"], ["approved", "Approved+"], ["declined", "Declined"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={f.account_status} onChange={(e) => setF({ ...f, account_status: e.target.value })} className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5">
          {[["", "Account: any"], ["pending", "Pending+"], ["active", "Active"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={f.reservation_status} onChange={(e) => setF({ ...f, reservation_status: e.target.value })} className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5">
          {[["", "Reservation: any"], ["requested", "Requested+"], ["approved", "Approved+"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={f.recognition_status} onChange={(e) => setF({ ...f, recognition_status: e.target.value })} className="text-xs bg-black/40 border border-white/15 rounded-md px-2 py-1.5">
          {[["", "Recognition: any"], ["has", "Has recognition"], ["none", "None"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="text-xs text-muted-foreground flex items-center gap-1.5"><input type="checkbox" checked={f.first_stay === "completed"} onChange={(e) => setF({ ...f, first_stay: e.target.checked ? "completed" : "" })} className="accent-[#D4AF37]" /> First stay</label>
        <label className="text-xs text-muted-foreground flex items-center gap-1.5"><input type="checkbox" checked={f.follow_up === "true"} onChange={(e) => setF({ ...f, follow_up: e.target.checked ? "true" : "" })} className="accent-[#D4AF37]" data-testid="filter-followup" /> Follow-up</label>
        {(f.preset || f.member || f.source || f.invitation_status || f.access_status || f.account_status || f.reservation_status || f.recognition_status || f.first_stay || f.follow_up) &&
          <button onClick={() => setF({ preset: "", member: "", level: "", source: "", invitation_status: "", access_status: "", account_status: "", reservation_status: "", first_stay: "", follow_up: "", recognition_status: "" })} className="text-xs text-gold hover:underline">Clear</button>}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setStage("")} className={`text-xs px-3 py-1.5 rounded-full border ${!stage ? "border-gold text-gold bg-gold/10" : "border-white/15 text-muted-foreground"}`}>All</button>
        {STAGES.slice(0, 13).map((st) => (
          <button key={st} onClick={() => setStage(st)} className={`text-xs px-3 py-1.5 rounded-full border ${stage === st ? "border-gold text-gold bg-gold/10" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>{st}</button>
        ))}
      </div>

      <Panel>
        {rows.length === 0 ? (
          <div className="py-14 text-center text-muted-foreground"><Share2 className="mx-auto mb-3 opacity-40" size={28} /><p className="text-sm">No referrals{stage ? ` at "${stage}"` : ""} yet.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left"><Th>Referring Member</Th><Th>Referred Person</Th><Th>Source</Th><Th>Stage</Th><Th>Last Activity</Th><Th></Th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => openDetail(r)} data-testid={`referral-row-${r.id}`}>
                    <Td>{r.referring_member_name || "—"}</Td>
                    <Td className="text-muted-foreground">{r.referred_name}</Td>
                    <Td className="text-muted-foreground">{r.referral_source || r.relationship || "—"}</Td>
                    <Td><span className="text-[10px] px-2 py-0.5 rounded-full border border-gold/30 text-gold bg-gold/5">{r.stage}</span></Td>
                    <Td className="text-muted-foreground whitespace-nowrap">{fmtDate(r.last_activity_at)}</Td>
                    <Td className="text-gold text-xs">Open →</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowNew(false)}>
          <div className="glass-dark border border-gold/30 rounded-2xl max-w-md w-full mt-10 p-6" onClick={(e) => e.stopPropagation()} data-testid="referral-new-modal">
            <h3 className="font-serif text-2xl mb-4">New Member Referral</h3>
            <div className="space-y-3">
              <input className={inputCls} placeholder="Referring member name" value={nf.referring_member_name} onChange={(e) => setNf({ ...nf, referring_member_name: e.target.value })} />
              <input className={inputCls} placeholder="Referred person name *" value={nf.referred_name} onChange={(e) => setNf({ ...nf, referred_name: e.target.value })} data-testid="referral-new-name" />
              <input className={inputCls} placeholder="Referred person email" value={nf.referred_email} onChange={(e) => setNf({ ...nf, referred_email: e.target.value })} />
              <input className={inputCls} placeholder="Relationship" value={nf.relationship} onChange={(e) => setNf({ ...nf, relationship: e.target.value })} />
              <input className={inputCls} placeholder="Suggested experience" value={nf.suggested_experience} onChange={(e) => setNf({ ...nf, suggested_experience: e.target.value })} />
              <textarea rows={2} className={inputCls} placeholder="Reason for referral" value={nf.reason} onChange={(e) => setNf({ ...nf, reason: e.target.value })} />
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={createRef} className="btn-gold px-4 py-2 text-sm" data-testid="referral-new-save">Create referral</button>
              <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm text-muted-foreground">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="glass-dark border border-gold/30 rounded-2xl max-w-2xl w-full mt-8 mb-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="referral-detail">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-serif text-2xl">{detail.referring_member_name || "Member"} → {detail.referred_name}</h3>
                <p className="text-sm text-muted-foreground">{detail.relationship}{detail.referred_email ? ` · ${detail.referred_email}` : ""}</p>
                <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border border-gold/30 text-gold bg-gold/5">{detail.stage}</span>
              </div>
              <button onClick={() => setDetail(null)} className="text-muted-foreground hover:text-gold"><X size={18} /></button>
            </div>

            {detail.reason && <p className="text-sm glass rounded-lg px-3 py-2 mb-4">{detail.reason}</p>}

            <div className="mb-4">
              <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Advance stage</p>
              <select className={inputCls} value="" onChange={(e) => e.target.value && advance(e.target.value)} data-testid="referral-advance">
                <option value="">Select next stage…</option>
                {STAGES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>

            <div className="mb-4">
              <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Conversion stage history</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="referral-history">
                {(detail.stage_history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="text-sm flex items-center gap-2">
                    <ChevronRight size={12} className="text-gold shrink-0" />
                    <span>{h.previous_stage ? `${h.previous_stage} → ` : ""}<b>{h.stage}</b></span>
                    <span className="text-[10px] text-muted-foreground ml-auto whitespace-nowrap">{fmtDate(h.at)} · {h.by}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-3 mb-4">
              <button onClick={summarize} disabled={aiBusy} className="btn-ghost-gold px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="referral-ai">{aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Summarize with D</button>
              {ai && (
                <div className="mt-3 glass rounded-lg p-3" data-testid="referral-ai-result">
                  <p className="text-[10px] tracking-label uppercase text-gold mb-1">{ai.label}</p>
                  <p className="text-sm whitespace-pre-wrap text-foreground/90">{ai.text}</p>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 pt-4">
              <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2 flex items-center gap-1.5"><Award size={12} /> Private recognition (owner)</p>
              {(detail.recognitions || []).map((rr) => (
                <div key={rr.id} className="glass rounded-lg px-3 py-2 text-sm mb-2"><span className="text-gold text-xs">{rr.recognition_type}</span><p>{rr.note}</p></div>
              ))}
              <div className="flex gap-2">
                <select className={inputCls + " max-w-[180px]"} value={rec.recognition_type} onChange={(e) => setRec({ ...rec, recognition_type: e.target.value })}>
                  {RECOGNITION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input className={inputCls} placeholder="Private note (required)" value={rec.note} onChange={(e) => setRec({ ...rec, note: e.target.value })} data-testid="recognition-note" />
                <button onClick={addRecognition} className="btn-gold px-3 py-2 text-xs whitespace-nowrap" data-testid="recognition-save">Record</button>
              </div>
              <p className="text-[10px] text-muted-foreground/70 mt-2">No automatic reward, promotion, or public leaderboard. Owner-approved only.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
